import clsx from 'clsx'
import React, { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import CardPhotoCarousel from 'src/components/elements/card-photo-carousel/CardPhotoCarousel'
import PromoteModal from 'src/components/elements/modals/promote/PromoteModal'
import { Button } from 'src/components/ui'
import { formatPrice } from 'src/helpers/formatPrice'
import { pricePeriodKey } from 'src/helpers/pricePeriod'
import { propertyTypeLabel } from 'src/helpers/propertyTypeLabel'
import { IMyProperty, useGetPromotionPlansQuery } from 'src/redux/api'
import ViewsSparkline from './ViewsSparkline'

const statusClasses: Record<IMyProperty['status'], string> = {
  ACTIVE: 'bg-success-soft text-success',
  PENDING: 'bg-warning-soft text-warning',
  REJECTED: 'bg-danger-soft text-danger',
  COMPLETED: 'bg-muted-soft text-muted',
}

const Stat: FC<{ icon: JSX.Element; label: string; value: number }> = ({ icon, label, value }) => (
  <span className="inline-flex items-center gap-1" title={label}>
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {icon}
    </svg>
    <span>
      <span className="sr-only">{label}: </span>
      {value}
    </span>
  </span>
)

interface Props extends IMyProperty {
  className?: string
}

// The owner's own listing as a row: status, the numbers that matter to a
// seller (views, requests, contacts) and the actions (edit, promote) in one
// place. It used to be a third card style next to the feed cards.
const MyPropertyCard: FC<Props> = ({
  id,
  title,
  listingType,
  propertyType,
  price,
  images,
  status,
  viewsCount,
  requestsCount,
  contactsCount,
  rejectionReason,
  promotedUntil,
  isPromoted,
  className,
}) => {
  const { t } = useTranslation()
  const { data: plansData } = useGetPromotionPlansQuery(undefined)
  const [openPromote, setOpenPromote] = useState(false)
  const canPromote = Boolean(plansData?.enabled) && status === 'ACTIVE'
  const periodKey = pricePeriodKey(listingType)
  const href = `/announcement/${id}`

  return (
    <>
      <article
        data-card="owner-listing"
        className={clsx('flex gap-3.5 p-3 bg-surface rounded-2xl shadow-card', className)}
      >
        <Link
          to={href}
          tabIndex={-1}
          aria-hidden="true"
          className="relative shrink-0 w-[7.5rem] h-28 rounded-xl overflow-hidden"
        >
          <CardPhotoCarousel images={images || []} alt={title} className="w-full h-full" />
          {isPromoted && (
            <span className="absolute top-2 left-2 px-1.5 py-0.5 text-xs font-black text-white rounded-md bg-brand-strong">
              {t('promote.badge_top')}
            </span>
          )}
        </Link>
        <div className="flex-1 min-w-0 flex flex-col gap-1.5">
          <div className="flex items-start gap-2">
            <Link
              to={href}
              className="flex-1 min-w-0 text-lg font-black leading-6 text-ink hover:text-brand-ink truncate"
            >
              {formatPrice(price?.amount, price?.currency)}
              {periodKey && <span className="text-sm font-bold text-ink-2"> {t(periodKey)}</span>}
            </Link>
            <span
              className={clsx(
                'shrink-0 px-2 py-0.5 rounded-lg text-xs font-extrabold',
                statusClasses[status],
              )}
            >
              {t(`my_offers.status.${status.toLowerCase()}`)}
            </span>
          </div>
          <span className="text-sm font-bold text-ink truncate">
            {/* the title already starts with the type ("Квартира, Київ") */}
            {title || propertyTypeLabel(t, propertyType)}
          </span>
          <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[0.8125rem] text-ink-2">
            <Stat
              label={t('my_offers.views')}
              value={viewsCount}
              icon={
                <>
                  <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" />
                  <circle cx="12" cy="12" r="3" />
                </>
              }
            />
            <Stat
              label={t('my_offers.requests')}
              value={requestsCount}
              icon={
                <>
                  <path d="M4 13l3-8h10l3 8v6H4z" />
                  <path d="M4 13h5l1 2h4l1-2h5" />
                </>
              }
            />
            {typeof contactsCount === 'number' && (
              <Stat
                label={t('my_offers.contacts')}
                value={contactsCount}
                icon={
                  <path d="M6 3h4l2 5-2.5 1.5a11 11 0 0 0 5 5L16 12l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 4 5a2 2 0 0 1 2-2z" />
                }
              />
            )}
            <ViewsSparkline propertyId={id} />
          </div>
          {status === 'REJECTED' && (
            <div className="text-[0.8125rem]">
              {rejectionReason && (
                <div className="text-danger">
                  {t('my_offers.rejection_reason')}: {rejectionReason}
                </div>
              )}
              {/* Saving an edit sends the listing back to moderation — say so,
                  a rejected listing used to look like a dead end. */}
              <div className="text-ink-2">{t('my_offers.rejected_hint')}</div>
            </div>
          )}
          <div className="flex flex-wrap gap-2 mt-auto pt-1">
            <Button size="sm" variant="outlined" link={`/edit-ad/${id}`}>
              {t('offer.edit')}
            </Button>
            {canPromote && (
              <Button
                size="sm"
                variant={isPromoted ? 'neutral' : 'primary'}
                onClick={() => setOpenPromote(true)}
              >
                {isPromoted && promotedUntil
                  ? t('promote.active_until', {
                      date: format(new Date(promotedUntil), 'dd.MM.yyyy'),
                    })
                  : t('promote.button')}
              </Button>
            )}
          </div>
        </div>
      </article>
      {canPromote && (
        <PromoteModal
          open={openPromote}
          handleClose={() => setOpenPromote(false)}
          propertyId={id}
          title={title}
          image={images?.[0]}
          price={price}
        />
      )}
    </>
  )
}

export default MyPropertyCard
